import openai from '../lib/openai';

/**
 * Extract archaic words from Shakespeare text and generate definitions with pronunciation guides
 * @param {string} shakespeareText - The Shakespeare text to analyze
 * @param {string} playTitle - The title of the play (optional, for context)
 * @returns {Promise<Array>} Array of glossary terms with definitions and pronunciations
 */
export const generateGlossaryFromText = async (shakespeareText, playTitle = '') => {
  try {
    if (!shakespeareText || shakespeareText?.trim()?.length === 0) {
      throw new Error('Shakespeare text is required');
    }

    const prompt = `You are a Shakespeare scholar and linguist. Analyze the following Shakespeare text${playTitle ? ` from "${playTitle}"` : ''} and identify archaic or difficult words that modern students would need help understanding.

For each archaic word, provide:
1. The word itself
2. A clear, concise definition (1-2 sentences)
3. Pronunciation guide in IPA (International Phonetic Alphabet) format
4. Difficulty level (easy, medium, hard)

Text to analyze:
"${shakespeareText}"

Return ONLY a valid JSON array with this exact structure:
[
  {
    "word": "thee",
    "definition": "An archaic second-person singular pronoun meaning 'you' (as the object of a verb or preposition).",
    "pronunciation": "/ðiː/",
    "difficulty": "easy"
  }
]

Rules:
- Only include genuinely archaic or difficult words (not common modern words)
- Avoid duplicates
- Focus on words that would challenge high school or college students
- Difficulty: easy (common archaic words), medium (moderately difficult), hard (very obscure)
- Return ONLY the JSON array, no additional text or explanation`;

    const response = await openai?.chat?.completions?.create({
      model: 'gpt-4o-mini',
      messages: [
        {
          role: 'system',
          content: 'You are a Shakespeare scholar and expert linguist specializing in Early Modern English. You provide accurate, educational definitions and pronunciation guides for archaic words.'
        },
        {
          role: 'user',
          content: prompt
        }
      ],
      temperature: 0.3, // Lower temperature for more consistent, factual responses
      max_tokens: 2000,
      response_format: { type: 'json_object' }
    });

    const content = response?.choices?.[0]?.message?.content;
    
    if (!content) {
      throw new Error('No response from OpenAI');
    }

    // Parse the JSON response
    let parsedResponse;
    try {
      parsedResponse = JSON.parse(content);
    } catch (parseError) {
      console.error('Failed to parse OpenAI response:', content);
      throw new Error('Invalid response format from AI');
    }

    // Handle different response structures
    let glossaryTerms = [];
    if (Array.isArray(parsedResponse)) {
      glossaryTerms = parsedResponse;
    } else if (parsedResponse?.words && Array.isArray(parsedResponse?.words)) {
      glossaryTerms = parsedResponse?.words;
    } else if (parsedResponse?.terms && Array.isArray(parsedResponse?.terms)) {
      glossaryTerms = parsedResponse?.terms;
    } else {
      throw new Error('Unexpected response structure from AI');
    }

    // Validate and normalize the terms
    const validatedTerms = glossaryTerms?.filter(term => term?.word && term?.definition)?.map(term => ({
        word: term?.word?.trim(),
        definition: term?.definition?.trim(),
        pronunciation: term?.pronunciation?.trim() || '',
        difficulty: ['easy', 'medium', 'hard']?.includes(term?.difficulty) ? term?.difficulty : 'medium',
        category: 'archaic' // Default category for auto-generated terms
      }));

    if (validatedTerms?.length === 0) {
      throw new Error('No valid archaic words found in the text');
    }

    return validatedTerms;
  } catch (error) {
    console.error('Error generating glossary from text:', error);
    throw error;
  }
};

/**
 * Generate a single definition and pronunciation for a specific word
 * @param {string} word - The word to define
 * @param {string} context - Optional context sentence where the word appears
 * @returns {Promise<Object>} Object with definition and pronunciation
 */
export const generateSingleWordDefinition = async (word, context = '') => {
  try {
    if (!word || word?.trim()?.length === 0) {
      throw new Error('Word is required');
    }

    const prompt = `Define the archaic or Shakespearean word "${word}"${context ? ` as used in this context: "${context}"` : ''}.

Provide:
1. A clear, concise definition (1-2 sentences)
2. Pronunciation guide in IPA format
3. Difficulty level (easy, medium, hard)

Return ONLY a valid JSON object with this exact structure:
{
  "definition": "A clear definition here",
  "pronunciation": "/pronunciation/",
  "difficulty": "medium"
}`;

    const response = await openai?.chat?.completions?.create({
      model: 'gpt-4o-mini',
      messages: [
        {
          role: 'system',
          content: 'You are a Shakespeare scholar providing accurate definitions and pronunciations for archaic words.'
        },
        {
          role: 'user',
          content: prompt
        }
      ],
      temperature: 0.3,
      max_tokens: 300,
      response_format: { type: 'json_object' }
    });

    const content = response?.choices?.[0]?.message?.content;
    
    if (!content) {
      throw new Error('No response from OpenAI');
    }

    const parsed = JSON.parse(content);
    
    return {
      definition: parsed?.definition?.trim() || '',
      pronunciation: parsed?.pronunciation?.trim() || '',
      difficulty: ['easy', 'medium', 'hard']?.includes(parsed?.difficulty) ? parsed?.difficulty : 'medium'
    };
  } catch (error) {
    console.error('Error generating single word definition:', error);
    throw error;
  }
};
